import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ShieldCheck, Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = "/api";

const REASON_TEXT = {
  not_found: "Este enlace de invitación no es válido.",
  expired: "Esta invitación caducó. Pídele a la persona que te la envíe de nuevo.",
  revoked: "Esta invitación fue revocada.",
  declined: "Esta invitación ya fue rechazada.",
  already_accepted: "Esta invitación ya fue aceptada.",
};

export default function TrustedContactInvitation() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState({ loading: true, data: null });
  const [working, setWorking] = useState(false);
  const [done, setDone] = useState(null);

  const validate = useCallback(async () => {
    if (!token) {
      setState({ loading: false, data: { valid: false, reason: "not_found" } });
      return;
    }
    try {
      const r = await axios.get(`${API}/public/trusted-contact-invitation`, { params: { token } });
      setState({ loading: false, data: r.data });
    } catch (_e) {
      setState({ loading: false, data: { valid: false, reason: "not_found" } });
    }
  }, [token]);

  useEffect(() => { validate(); }, [validate]);

  const accept = async () => {
    if (!isAuthenticated) {
      navigate(`/login?next=${encodeURIComponent(`/contacto-de-confianza?token=${token}`)}`);
      return;
    }
    setWorking(true);
    try {
      await axios.post(`${API}/trusted-contacts/accept`, { token });
      setDone("accepted");
      toast.success("Invitación aceptada");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail === "invitation_target_mismatch"
        ? "Esta invitación fue enviada a otro correo. Inicia sesión con ese correo."
        : "No pudimos aceptar la invitación");
    } finally {
      setWorking(false);
    }
  };

  const decline = async () => {
    setWorking(true);
    try {
      await axios.post(`${API}/public/trusted-contacts/decline`, { token });
      setDone("declined");
    } catch (_e) {
      toast.error("No pudimos rechazar la invitación");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4 py-12">
      <main className="w-full max-w-lg bg-white border border-slate-200 rounded-2xl p-6 md:p-8"
            data-testid="invitation-card">
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck className="w-8 h-8 text-[#1B84FF]" aria-hidden="true" />
          <h1 className="text-2xl md:text-3xl font-bold text-[#0B2545]">Contacto de confianza</h1>
        </div>

        {state.loading && (
          <div className="flex justify-center py-10" data-testid="invitation-loading">
            <Loader2 className="w-6 h-6 animate-spin text-[#1B84FF]" />
          </div>
        )}

        {!state.loading && done === "accepted" && (
          <div role="status" data-testid="invitation-accepted">
            <CheckCircle2 className="w-10 h-10 text-[#1B84FF] mb-4" aria-hidden="true" />
            <p className="text-[#0B2545] font-semibold">Listo, ya eres contacto de confianza.</p>
            <p className="text-slate-600 text-sm mt-2">
              Recibirás los avisos de los perfiles que esa persona te asigne.
            </p>
            <Link to="/dashboard">
              <Button className="mt-6 bg-[#1B84FF] hover:bg-[#0F6FDB] text-white font-semibold"
                      data-testid="invitation-go-dashboard">Ir a mi panel</Button>
            </Link>
          </div>
        )}

        {!state.loading && done === "declined" && (
          <div role="status" data-testid="invitation-declined">
            <XCircle className="w-10 h-10 text-slate-500 mb-4" aria-hidden="true" />
            <p className="text-[#0B2545] font-semibold">Invitación rechazada.</p>
            <p className="text-slate-600 text-sm mt-2">No recibirás avisos de esta persona.</p>
          </div>
        )}

        {!state.loading && !done && state.data && !state.data.valid && (
          <div role="alert" data-testid="invitation-invalid">
            <AlertTriangle className="w-10 h-10 text-amber-400 mb-4" aria-hidden="true" />
            <p className="text-[#0B2545] font-semibold">
              {REASON_TEXT[state.data.reason] || "Este enlace no se puede usar."}
            </p>
            <Link to="/">
              <Button variant="ghost" className="mt-6 text-slate-600"
                      data-testid="invitation-home">Volver al inicio</Button>
            </Link>
          </div>
        )}

        {!state.loading && !done && state.data?.valid && (
          <div data-testid="invitation-valid">
            <p className="text-slate-600 text-sm md:text-base">
              <strong className="text-[#0B2545]">{state.data.owner_name || "Una persona"}</strong> te
              invitó a ser su contacto de confianza en AvisoCar.
            </p>
            <ul className="mt-5 space-y-2 text-sm text-slate-600 list-disc list-inside">
              <li>Recibirás avisos cuando alguien escanee los QR que te asigne.</li>
              <li>El contacto es privado: nadie comparte números de teléfono.</li>
              <li>Puedes dejar de ser contacto en cualquier momento.</li>
            </ul>
            {!isAuthenticated && (
              <p className="mt-5 text-xs text-amber-700" data-testid="invitation-login-hint">
                Necesitas una cuenta AvisoCar para aceptar. Inicia sesión o crea tu cuenta.
              </p>
            )}
            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={accept} disabled={working}
                      className="bg-[#1B84FF] hover:bg-[#0F6FDB] text-white font-semibold"
                      data-testid="invitation-accept">
                {working ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  : isAuthenticated ? "Aceptar invitación" : "Iniciar sesión y aceptar"}
              </Button>
              <Button variant="ghost" onClick={decline} disabled={working}
                      className="text-slate-600 hover:text-[#0B2545]"
                      data-testid="invitation-decline">Rechazar</Button>
              {!isAuthenticated && (
                <Link to={`/register?next=${encodeURIComponent(`/contacto-de-confianza?token=${token}`)}`}>
                  <Button variant="ghost" className="text-[#1B84FF]"
                          data-testid="invitation-register">Crear cuenta</Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
